import React, { useState, useEffect } from 'react';
import { auth, functions } from '../firebase/config';
import { signInWithEmailAndPassword, signInWithCustomToken } from "firebase/auth";
import { httpsCallable } from 'firebase/functions';
import { platformAuthenticatorIsAvailable } from '@simplewebauthn/browser';

const ForgotPasswordModal = ({ isOpen, onClose, onSendLink }) => {
    const [email, setEmail] = useState('');
    if (!isOpen) return null;
    const handleSubmit = (e) => { e.preventDefault(); onSendLink(email); };
    return ( <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"><div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-xl"><h3 className="text-lg font-semibold text-gray-800 mb-4">Reset Password</h3><p className="text-gray-600 mb-4">Enter your email address and we will send you a link to reset your password.</p><form onSubmit={handleSubmit}><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 mb-4 border border-gray-300 rounded-md shadow-sm" placeholder="your@email.com" required /><div className="flex justify-end space-x-3"><button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button><button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Send Link</button></div></form></div></div> );
};

const LoginPage = () => {
    const [isLoginView, setIsLoginView] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isBiometricAvailable, setIsBiometricAvailable] = useState(false);
    const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);

    useEffect(() => {
        platformAuthenticatorIsAvailable().then(setIsBiometricAvailable);
    }, []);

    const handleAuthAction = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setSuccess('');

        if (isLoginView) {
            try {
                await signInWithEmailAndPassword(auth, email, password);
            } catch (err) {
                setError(err.message);
            }
        } else {
            if (password !== confirmPassword) {
                setError("Passwords do not match.");
                setIsLoading(false);
                return;
            }
            try {
                const createUserAccount = httpsCallable(functions, 'createUserAccount');
                await createUserAccount({ email, password });
                await signInWithEmailAndPassword(auth, email, password);
            } catch (err) {
                setError(err.message);
            }
        }
        setIsLoading(false);
    };
    
    const handleBiometricLogin = async () => {
        setIsLoading(true);
        setError('');
        setSuccess('');
        try {
            const getAuthOptions = httpsCallable(functions, 'getAuthenticationOptions');
            const options = await getAuthOptions();
            const { startAuthentication } = await import('@simplewebauthn/browser');
            const authResp = await startAuthentication(options.data);
            const verifyAuth = httpsCallable(functions, 'verifyAuthentication');
            const result = await verifyAuth({ assertion: authResp });
            if (result.data.verified) {
                await signInWithCustomToken(auth, result.data.token);
            } else {
                setError("Biometric login failed.");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendPasswordReset = async (resetEmail) => {
        setIsLoading(true);
        setError('');
        setSuccess('');
        try {
            const sendPasswordResetEmail = httpsCallable(functions, 'sendPasswordResetEmail');
            const result = await sendPasswordResetEmail({ email: resetEmail });
            setSuccess(result.data.message);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
            setIsForgotPasswordOpen(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
            <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-center text-gray-800">{isLoginView ? 'Login' : 'Create Account'}</h2>
                {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                {success && <p className="text-green-500 text-sm text-center">{success}</p>}
                <form onSubmit={handleAuthAction} className="space-y-6">
                    <div>
                        <label className="text-sm font-medium text-gray-700">Email</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm" required />
                    </div>
                    <div className="relative">
                        <label className="text-sm font-medium text-gray-700">Password</label>
                        <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm" required />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 top-6 pr-3 flex items-center text-sm leading-5">
                            {showPassword ? ( <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a9.97 9.97 0 01-1.563 3.029m-5.858-.908a3 3 0 00-4.243-4.243"/></svg> ) : ( <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.522 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.478 0-8.268-2.943-9.542-7z" /></svg> )}
                        </button>
                    </div>
                    {!isLoginView && (
                        <div className="relative">
                            <label className="text-sm font-medium text-gray-700">Confirm Password</label>
                            <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm" required />
                        </div>
                    )}
                    {isLoginView && <div className="text-sm"><button type="button" onClick={() => setIsForgotPasswordOpen(true)} className="font-medium text-blue-600 hover:text-blue-500">Forgot your password?</button></div>}
                    <div><button type="submit" disabled={isLoading} className="w-full px-4 py-2 font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300">{isLoading ? 'Processing...' : (isLoginView ? 'Login' : 'Sign Up')}</button></div>
                </form>
                <div className="text-center text-sm">
                    <button onClick={() => setIsLoginView(!isLoginView)} className="font-medium text-blue-600 hover:text-blue-500">
                        {isLoginView ? "Don't have an account? Sign Up" : "Already have an account? Login"}
                    </button>
                </div>
                {isBiometricAvailable && isLoginView && (
                    <>
                        <div className="relative my-4"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300"></div></div><div className="relative flex justify-center text-sm"><span className="px-2 bg-white text-gray-500">Or</span></div></div>
                        <button onClick={handleBiometricLogin} disabled={isLoading} className="w-full px-4 py-2 font-medium text-white bg-gray-700 rounded-md hover:bg-gray-800 disabled:bg-gray-400">{isLoading ? 'Checking...' : 'Login with Biometrics'}</button>
                    </>
                )}
            </div>
            <ForgotPasswordModal isOpen={isForgotPasswordOpen} onClose={() => setIsForgotPasswordOpen(false)} onSendLink={handleSendPasswordReset} />
        </div>
    );
};

export default LoginPage;